import {
  ActionPattern,
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'

import { secureSanitize } from '../../utils/sanitizeUtils'
import { getDayNumber } from '../../utils/TimeUtils'
import { Recipes } from '../../graphql/Services'
import { actions, selectors } from '..'
import ApiSagas from '../api/sagas'
import { Day, Moment } from '../../types/fam-graphql'
import { replaceRecipeInPlan } from '../../graphql/Services/User/mutations'
import Router from '../../routes/router'
import routes from '../../routes/definitions'
import { ServiceRequestAction } from '../../helpers/ReduxHelpers'

import { actionTypes } from './types'

export default class SwitchCourseSagas {
  static *init({ query }: any) {
    const { dayNumber, moment, id, side, tab } = query

    const currentPlan = yield select(selectors.user.currentPlan)
    if (!currentPlan) {
      yield put(actions.app.setIsLoading(true))
    }

    const sanitizedId = secureSanitize(id, true)
    const sanitizedSide = secureSanitize(side, true)
    const sanitizedTab = secureSanitize(tab, true)
    const sanitizedMoment = secureSanitize(moment, true)
    const sanitizedDayNumber = secureSanitize(dayNumber, true)

    return yield call(SwitchCourseSagas.loadPage, {
      ...query,
      day: `DAY_${getDayNumber(sanitizedDayNumber)}`,
      moment: sanitizedMoment,
      id: sanitizedId,
      side: sanitizedSide,
      selectedSide: sanitizedTab || 'course',
    })
  }

  static *loadPage({
    day,
    moment,
    id,
    side,
    selectedSide,
  }: {
    day: Day
    moment: Moment
    id: string
    side?: string
    selectedSide: string
  }) {
    const { data: recipeMaincomponent } = yield call(
      ApiSagas.query,
      Recipes.queries.recipeMainComponentById({
        id: selectedSide === 'course' ? id : side || id,
      })
    )
    yield put(
      actions.switchcourse.setInput({
        day,
        moment,
        recipe: id,
        side,
        recipeMaincomponent: recipeMaincomponent || null,
      })
    )

    const { data: recipe } = yield call(
      ApiSagas.query,
      Recipes.queries.findReplacement({
        id,
        page: 1,
        first: 9,
        recipeMainComponent: true,
      })
    )

    yield put(actions.switchcourse.setRecipes(recipe))

    if (!recipe) {
      return { error: 404 }
    }

    if (!side) {
      yield put(actions.switchcourse.setSides(null))
      return
    }

    // In case a recipe is composed by "COURSE" + "SIDE"
    const { data: sideData } = yield call(
      ApiSagas.query,
      Recipes.queries.findReplacement({
        id: side,
        page: 1,
        first: 9,
        recipeMainComponent: true,
      })
    )

    yield put(actions.switchcourse.setSides(sideData))

    if (!sideData) {
      return { error: 404 }
    }
  }

  static *loadRecipes(action: actionTypes.loadRecipes) {
    const { page, id, selectedSide, recipeMainComponent } = action.payload

    const { data: result } = yield call(
      ApiSagas.query,
      Recipes.queries.findReplacement({
        id: id ?? '',
        first: page * 9,
        page: 1,
        recipeMainComponent,
      })
    )

    if (selectedSide === 'course') {
      yield put(actions.switchcourse.setRecipes(result))
    } else if (selectedSide === 'side') {
      yield put(actions.switchcourse.setSides(result))
    }

    if (!result) {
      return { error: 404 }
    }
  }

  static *replaceMainComponent(action: ServiceRequestAction) {
    const { payload } = action
    const { day, moment, id, side, selectedSide } = payload?.params

    const { data: recipeMaincomponent } = yield call(
      ApiSagas.query,
      Recipes.queries.recipeMainComponentById({
        id: selectedSide === 'course' ? id : side ?? id,
      })
    )
    yield put(
      actions.switchcourse.setInput({
        day,
        moment,
        recipe: id,
        side,
        recipeMaincomponent: recipeMaincomponent || null,
      })
    )
  }

  static *replaceRecipeInPlan(action: ServiceRequestAction) {
    const { payload } = action
    const { data: results } = yield call(
      ApiSagas.mutate,
      replaceRecipeInPlan(payload?.params)
    )

    if (!results.error) {
      yield put(actions.switchcourse.replaceRecipeInPlanSuccess({}))
      yield put(actions.user.setCurrentPlan(results))
      Router.pushRoute(routes.planning.name, {
        jour: getDayNumber(payload?.params?.day),
      })
    } else {
      yield put(actions.switchcourse.replaceRecipeInPlanError(results.error))
    }
  }

  static *loop() {
    yield all([
      //
      takeLatest(
        actions.switchcourse.loadRecipes as ActionPattern,
        SwitchCourseSagas.loadRecipes
      ),
      takeLatest(
        actions.switchcourse.replaceRecipeInPlanRequest as ActionPattern,
        SwitchCourseSagas.replaceRecipeInPlan
      ),
      takeLatest(
        actions.switchcourse.replaceMainComponentRequest as ActionPattern,
        SwitchCourseSagas.replaceMainComponent
      ),
    ])
  }
}
